import { Linking, Platform } from 'react-native';
import { api } from './client';
import { appPlatform, appVersion } from './appVersion';

/**
 * Whether this build is still allowed to run.
 *
 * ---------------------------------------------------------------------------
 * EVERY FAILURE HERE MEANS "CARRY ON"
 * ---------------------------------------------------------------------------
 * The server fails open by design and so does this. No network, a 500, a
 * timeout, a shape that does not parse - all of them resolve to "not blocked".
 *
 * The reason is that a gate is unrecoverable from the wrong side. If this ever
 * wrongly returns `true`, the customer sees a wall with one button on it and
 * no way past; there is no screen behind it to fix anything from, no support
 * chat, no sign-in. Getting it wrong costs a store release - hours on Android,
 * days on iOS - during which the app is unusable for everybody.
 *
 * Against that, letting an out-of-date build run for one more session is
 * nothing. So this never throws and never blocks on doubt.
 */

/**
 * Where to send somebody the server did not give a link for.
 *
 * ---------------------------------------------------------------------------
 * TWO OF THESE THREE OPENED NOTHING
 * ---------------------------------------------------------------------------
 * This is the link on `ForceUpdateGate` - a full-screen wall with one button
 * and no way past it. A dead link there leaves a customer holding an app they
 * cannot use and nothing to tap, which is the worst place in the app for a
 * URL nobody checked.
 *
 *  - `homingo.com` does not resolve AT ALL. Checked again on 12 Sep 2026:
 *    the request does not complete. The live host is `api.itshomingo.com`,
 *    which serves `/support` and `/legal/*` as real pages (200). The Pro app
 *    found and fixed the same fallback - see its `config/links.ts`.
 *  - `id0000000000` is a placeholder Apple ID, so the iOS link is a
 *    guaranteed App Store 404. This app has never been submitted (no Xcode
 *    build yet), so there is no real record to point at either. The support
 *    page is at least a page with a human behind it. REPLACE THIS with the
 *    real `apps.apple.com/app/idNNNNNNNNN` the day the listing exists - the
 *    Pro app's is 6809687440, which is a DIFFERENT app and must not be reused.
 *
 * Android is left alone: `com.homingo` is the real package and the Play
 * listing resolves.
 */
const SUPPORT_PAGE = 'https://api.itshomingo.com/support';

const FALLBACK_STORE_URL = Platform.select({
  android: 'https://play.google.com/store/apps/details?id=com.homingo',
  ios: SUPPORT_PAGE,
  default: SUPPORT_PAGE,
}) as string;

export type UpdateGate = {
  updateRequired: boolean;
  minVersion: string | null;
  currentVersion: string | null;
  /** Always populated - the server's link, or ours. */
  storeUrl: string;
};

const NOT_BLOCKED: UpdateGate = {
  updateRequired: false,
  minVersion: null,
  currentVersion: null,
  storeUrl: FALLBACK_STORE_URL,
};

/**
 * Ask the server.
 *
 * `anonymous`, because the build most likely to be blocked belongs to somebody
 * who has not opened the app in months and whose refresh token is long dead.
 * Sending a stale bearer would trip the 401 handler and bounce them to sign-in
 * - from a build that cannot sign in, which is the whole problem.
 */
export async function checkForUpdate(
  signal?: AbortSignal,
): Promise<UpdateGate> {
  /*
   * A web deploy is always its own newest version - reloading the tab IS the
   * update. Asking the gate anyway would report `appVersion()`'s fallback,
   * which is the ANDROID build number, and the day that number drops below
   * the Android minimum the website would wall itself off and point everyone
   * at the Play Store. Not a risk worth running for a check that means
   * nothing here.
   */
  if (Platform.OS === 'web') {
    return NOT_BLOCKED;
  }

  try {
    const result = await api.get<Partial<UpdateGate> | null>(
      '/app/version-check',
      {
        query: {
          app: 'customer',
          platform: appPlatform(),
          version: appVersion(),
        },
        anonymous: true,
        signal,
      },
    );

    // `=== true` rather than truthy: a server that answers `"yes"`, `1` or an
    // error object must not be read as a block. Only the exact boolean counts.
    if (result?.updateRequired !== true) return NOT_BLOCKED;

    return {
      updateRequired: true,
      minVersion: result.minVersion ?? null,
      currentVersion: result.currentVersion ?? appVersion(),
      storeUrl: result.storeUrl?.trim() || FALLBACK_STORE_URL,
    };
  } catch (error) {
    console.error('[update] version check failed, letting the app run', error);
    return NOT_BLOCKED;
  }
}

/**
 * Open the store listing.
 *
 * Failure is logged rather than surfaced: the gate has no other action, so a
 * message saying the button did not work leaves somebody with a dead screen
 * and no next step. The link is also on the screen as text for that reason.
 */
export async function openStore(url: string): Promise<void> {
  try {
    await Linking.openURL(url);
  } catch (error) {
    console.error(`[update] could not open the store at ${url}`, error);
  }
}
